import { Upload, Modal, Button, Typography, Space, Progress, Tooltip } from "antd";
import {
    UploadOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    CloseOutlined,
    CheckCircleFilled,
    ExclamationCircleFilled,
    DeleteOutlined,
    LoadingOutlined
} from "@ant-design/icons";
import { useState } from "react";
import { Method_POST_UPLOAD } from "../../../../../service/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { Text, Title } = Typography;
const { Dragger } = Upload;

export default function ImportarArquivo({ open, onClose, tipoArquivo, onShowInstructions }) {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);

    const validarOFX = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const conteudo = e.target.result;
                if (!/^OFXHEADER/i.test(conteudo) && !/<OFX>/i.test(conteudo)) {
                    reject("Arquivo OFX inválido. Certifique-se de que é um OFX XML ou SGML válido.");
                } else {
                    resolve(true);
                }
            };
            reader.onerror = () => reject("Erro ao ler o arquivo");
            reader.readAsText(file);
        });
    };

    const updateFileStatus = (uid, status, errorMsg = '') => {
        setSelectedFiles(prev => prev.map(item =>
            item.file.uid === uid ? { ...item, status, errorMsg } : item
        ));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            toast.error("Selecione pelo menos um arquivo antes de carregar");
            return;
        }

        setUploading(true);
        let successCount = 0;
        let errorCount = 0;

        const url = tipoArquivo === "excel"
            ? "/contabil/lancamento/import/excel"
            : "/contabil/lancamento/import/ofx";

        for (const item of selectedFiles) {
            if (item.status === 'success') continue;

            const file = item.file;
            updateFileStatus(file.uid, 'uploading');

            if (tipoArquivo === "ofx") {
                try {
                    await validarOFX(file);
                } catch (err) {
                    updateFileStatus(file.uid, 'error', err);
                    errorCount++;
                    continue;
                }
            }

            try {
                const response = await Method_POST_UPLOAD(url, file);

                if (response.erro) {
                    const { mensagem } = response.erro;
                    updateFileStatus(file.uid, 'error', mensagem);
                    errorCount++;
                } else {
                    updateFileStatus(file.uid, 'success');
                    successCount++;
                }
            } catch (err) {
                console.error(`Erro ao enviar arquivo ${file.name}:`, err);
                updateFileStatus(file.uid, 'error', 'Erro na conexão com o servidor');
                errorCount++;
            }
        }

        if (successCount > 0) {
            toast.success(`${successCount} arquivo(s) processado(s) com sucesso!`);
        }

        setUploading(false);

        if (errorCount === 0 && successCount > 0) {
            setTimeout(() => {
                handleClose();
            }, 2000);
        }
    };

    const handleClose = () => {
        setSelectedFiles([]);
        setUploading(false);
        onClose();
    };

    const handleRemoveFile = (uid) => {
        setSelectedFiles(prev => prev.filter(item => item.file.uid !== uid));
    };

    const handleRemoveAll = () => {
        setSelectedFiles([]);
    };

    const uploadProps = {
        accept: tipoArquivo === 'excel' ? ".xls,.xlsx" : tipoArquivo === 'ofx' ? ".ofx" : ".csv",
        multiple: true,
        showUploadList: false,
        beforeUpload: (file) => {
            const fileExtension = file.name.split(".").pop().toLowerCase();
            const isValid = (tipoArquivo === "excel" && ["xls", "xlsx"].includes(fileExtension)) ||
                (tipoArquivo === "ofx" && fileExtension === "ofx") ||
                (tipoArquivo === "csv" && fileExtension === "csv");

            if (!isValid) {
                toast.error(`Tipo de arquivo inválido: ${file.name}`);
                return Upload.LIST_IGNORE;
            }

            setSelectedFiles(prev => {
                if (prev.find(item => item.file.name === file.name && item.file.size === file.size)) {
                    return prev;
                }
                return [...prev, { file, status: 'pending', errorMsg: '' }];
            });
            return false;
        },
    };

    const getIcon = (item) => {
        if (item.status === 'uploading') return <LoadingOutlined style={{ color: '#6366f1' }} />;
        if (item.status === 'success') return <CheckCircleFilled style={{ color: '#10b981' }} />;
        if (item.status === 'error') return <ExclamationCircleFilled style={{ color: '#ef4444' }} />;

        return tipoArquivo === 'excel' ?
            <FileExcelOutlined style={{ color: '#16a34a' }} /> :
            <FileTextOutlined style={{ color: '#2563eb' }} />;
    };

    return (
        <Modal
            title={null}
            open={open}
            onCancel={handleClose}
            footer={null}
            style={{ top: 80 }}
            width={540}
            className="premium-modal-v2"
            destroyOnClose
        >
            <div style={{ padding: '8px' }}>
                <header style={{ marginBottom: 24, textAlign: 'center' }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: 16,
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        {tipoArquivo === 'excel' ? <FileExcelOutlined style={{ color: '#16a34a' }} /> : <FileTextOutlined style={{ color: '#2563eb' }} />}
                    </div>
                    <Title level={4} style={{ margin: 0 }}>
                        {tipoArquivo === 'excel' ? 'Importar Planilha Excel' : 'Importar Extrato OFX'}
                    </Title>
                    <Text type="secondary">Selecione os arquivos para processamento automático.</Text>
                </header>

                <Dragger {...uploadProps} className="premium-upload-dragger" disabled={uploading}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: '#6366f1' }} />
                    </p>
                    <p className="ant-upload-text">Clique ou arraste os arquivos aqui</p>
                    <p className="ant-upload-hint">Suporte a múltiplos arquivos simultâneos</p>
                </Dragger>

                {selectedFiles.length > 0 && (
                    <div className="custom-file-list-container" style={{ marginTop: 24 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <Text strong size="small">Arquivos Selecionados ({selectedFiles.length})</Text>
                            {!uploading && (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={handleRemoveAll}
                                >
                                    Limpar Tudo
                                </Button>
                            )}
                        </div>

                        <div className="custom-file-list">
                            {selectedFiles.map((item) => (
                                <div key={item.file.uid} className={`custom-file-item status-${item.status}`}>
                                    <div className="file-info" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div className="file-icon" style={{ fontSize: 24 }}>{getIcon(item)}</div>
                                        <div className="file-details">
                                            <Tooltip title={item.file.name}>
                                                <Text strong ellipsis style={{ maxWidth: 280, display: 'block' }}>
                                                    {item.file.name}
                                                </Text>
                                            </Tooltip>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                {(item.file.size / 1024).toFixed(1)} KB
                                                {item.errorMsg && <span style={{ color: '#ef4444', marginLeft: 8 }}>• {item.errorMsg}</span>}
                                            </Text>
                                        </div>
                                    </div>

                                    <div className="file-actions" style={{ marginLeft: 'auto' }}>
                                        {item.status === 'pending' && !uploading && (
                                            <Button
                                                type="text"
                                                icon={<CloseOutlined style={{ fontSize: 12 }} />}
                                                onClick={() => handleRemoveFile(item.file.uid)}
                                                className="remove-btn"
                                            />
                                        )}
                                        {item.status === 'uploading' && <Progress type="circle" percent={45} size={20} strokeColor="#6366f1" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div style={{
                    marginTop: 32,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 12
                }}>
                    <Button
                        onClick={handleClose}
                        size="large"
                        className="premium-btn-secondary"
                        disabled={uploading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleUpload}
                        loading={uploading}
                        disabled={selectedFiles.length === 0}
                        size="large"
                        className="premium-btn-primary"
                    >
                        {uploading ? 'Processando...' : 'Processar Arquivos'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

